import React, { useEffect, useState, useContext, useMemo } from "react";
import { Prompt } from "react-router-dom/cjs/react-router-dom.min";
import fetchHook from "../../hooks/fetchMSSQL-hook";
import { NavigationContext } from "../../context/navigation-context";
import { NotificationContext } from "../../context/notification-context";

export default function CustomerData() {
  const { setHeaderData } = useContext(NavigationContext);
  const { fetchMssqlApi } = fetchHook();
  const { notification } = useContext(NotificationContext);

  const [customer, setCustomer] = useState(null);
  const [initialCustomer, setInitialCustomer] = useState(null);
  const [changed, setChanged] = useState(false);
  const [availableToSave, setAvailableToSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [certificateFileName, setCertificateFileName] = useState("");
  const [privateKeyFileName, setPrivateKeyFileName] = useState("");
  const [us, setUS] = useState([]);

  const REGEX = {
    nip: /^[0-9]{10}$/,
    postalCode: /^[0-9]{2}-[0-9]{3}$/,
    city: /^[\p{L}0-9 .,'’\-\/()]+$/u,
    street: /^[\p{L}0-9 .,'’\-\/()]+$/u,
    houseNo: /^[\p{L}0-9\-\/]+$/u,
    flatNo: /^[\p{L}0-9\-\/]+$/u,
    postOffice: /^[\p{L}0-9 .,'’\-\/()]+$/u,
    invoiceEmail: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    bankAccount: /^[0-9 ]{26,35}$/,
  };

  const isEmpty = (v) => v === null || v === "" || typeof v === "undefined";
  const isString = (v) => typeof v === "string";
  const hasLenBetween = (v, min, max) =>
    isString(v) && v.length >= min && v.length <= max;
  const matches = (v, regex) => (isString(v) ? regex.test(v) : false);
  const optionalMatch = (value, regex) => {
    const v = String(value ?? "");
    return v === "" || regex.test(v);
  };

  const getCheckboxFromBool = (bool) => (
    <div className="d-flex justify-content-center align-items-center">
      <i
        className={bool ? "fas fa-check-square text-primary" : "far fa-square text-primary"}
        style={{ fontSize: 20 }}
      />
    </div>
  );

  const normalize = (res) => {
    const obj = Array.isArray(res) ? res[0] : res;
    if (!obj) return {};
    const next = { ...obj };

    if (
      next.RegulaminZgoda &&
      typeof next.RegulaminZgoda === "object" &&
      "data" in next.RegulaminZgoda
    ) {
      next.RegulaminZgoda = !!next.RegulaminZgoda?.data?.[0];
    }

    return next;
  };

  const load = () => {
    fetchMssqlApi(
      "customer",
      {},
      (res) => {
        const data = normalize(res);
        setInitialCustomer(data);
        setCustomer(data);
        setChanged(false);
        setCertificateFileName(data?.KsefCertificate ? "Plik zapisany w bazie" : "");
        setPrivateKeyFileName(data?.KsefPrivateKey ? "Plik zapisany w bazie" : "");
      },
      () => {
        notification("error", "Błąd pobierania");
      }
    );
  };

  const loadus = () => {
    fetchMssqlApi(
      "us",
      {},
      (res) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        setUS(data);
      },
      () => {
        notification("error", "Błąd pobierania urzędów skarbowych");
      }
    );
  };

  useEffect(() => {
    setHeaderData({ text: "Dane firmy" });
    load();
    loadus();
  }, []);

  const bothEmpty = (c) => {
    return (
      (c?.MinimumProfitabilityRate === null ||
        c?.MinimumProfitabilityRate === "" ||
        typeof c?.MinimumProfitabilityRate === "undefined") &&
      (c?.CriticalProductState === null ||
        c?.CriticalProductState === "" ||
        typeof c?.CriticalProductState === "undefined")
    );
  };

  const inRangeOrEmpty = (val, min, max) => {
    if (val === "" || val === null || typeof val === "undefined") return true;
    const n = Number(val);
    return !Number.isNaN(n) && n >= min && n <= max;
  };

  const nipLocked = Boolean(String(initialCustomer?.NIP || "").trim());
  const nazwaPodLocked = Boolean(String(initialCustomer?.NazwaPod || "").trim());
  const kodUsLocked = Boolean(String(initialCustomer?.KodUS || "").trim());

  const kodUsDisplay = useMemo(() => {
    if (!customer?.KodUS) return "";
    const found = us.find(
      (x) => String(x.KodUs ?? x.KodUS) === String(customer.KodUS)
    );
    if (!found) return customer.KodUS || "";
    return `${found.Nazwa} [${found.KodUs ?? found.KodUS}]`;
  }, [customer?.KodUS, us]);

  const validateEditable = (c) => {
    if (!c?.Name || !String(c.Name).trim()) return false;

    if (!nipLocked && !matches(String(c.NIP ?? ""), REGEX.nip)) return false;
    if (!nazwaPodLocked && !hasLenBetween(String(c.NazwaPod ?? ""), 1, 256)) return false;
    if (!kodUsLocked && !String(c.KodUS || "").trim()) return false;

    if (!inRangeOrEmpty(c.MinimumProfitabilityRate, 0, 10000)) return false;
    if (!inRangeOrEmpty(c.CriticalProductState, 0, 50)) return false;

    if (!hasLenBetween(String(c.AdresPod_ulica ?? ""), 1, 70) || !matches(String(c.AdresPod_ulica ?? ""), REGEX.street)) return false;
    if (!hasLenBetween(String(c.AdresPod_miejsc ?? ""), 1, 70) || !matches(String(c.AdresPod_miejsc ?? ""), REGEX.city)) return false;
    if (!matches(String(c.AdresPod_kodPoczt ?? ""), REGEX.postalCode)) return false;
    if (!hasLenBetween(String(c.AdresPod_nrDomu ?? ""), 1, 15) || !matches(String(c.AdresPod_nrDomu ?? ""), REGEX.houseNo)) return false;
    if (!hasLenBetween(String(c.AdresPod_nrLok ?? ""), 1, 15) || !matches(String(c.AdresPod_nrLok ?? ""), REGEX.flatNo)) return false;
    if (!hasLenBetween(String(c.AdresPod_poczta ?? ""), 1, 70) || !matches(String(c.AdresPod_poczta ?? ""), REGEX.postOffice)) return false;

    if (!optionalMatch(c.InvoiceEmail, REGEX.invoiceEmail)) return false;
    if (!optionalMatch(c.BankAccount, REGEX.bankAccount)) return false;

    return true;
  };

  const kasaErrors = useMemo(() => {
    if (!customer) return [];
    const e = [];

    if (!nipLocked && !matches(String(customer.NIP ?? ""), REGEX.nip)) {
      e.push("NIP: wymagane dokładnie 10 cyfr.");
    }

    if (!nazwaPodLocked && !hasLenBetween(customer.NazwaPod ?? "", 1, 256)) {
      e.push("Pełna nazwa podmiotu: wymagane, 1-256 znaków.");
    }

    if (!kodUsLocked && !String(customer.KodUS || "").trim()) {
      e.push("Urząd Skarbowy: wybór jest wymagany.");
    }

    if (!hasLenBetween(customer.AdresPod_ulica ?? "", 1, 70) || !matches(String(customer.AdresPod_ulica ?? ""), REGEX.street)) {
      e.push("Ulica: wymagane poprawne pole.");
    }
    if (!hasLenBetween(customer.AdresPod_miejsc ?? "", 1, 70) || !matches(String(customer.AdresPod_miejsc ?? ""), REGEX.city)) {
      e.push("Miejscowość: wymagane poprawne pole.");
    }
    if (!matches(String(customer.AdresPod_kodPoczt ?? ""), REGEX.postalCode)) {
      e.push("Kod pocztowy: wymagany wzór NN-NNN (np. 01-234).");
    }
    if (!hasLenBetween(customer.AdresPod_nrDomu ?? "", 1, 15) || !matches(String(customer.AdresPod_nrDomu ?? ""), REGEX.houseNo)) {
      e.push("Nr domu: wymagane poprawne pole.");
    }
    if (!hasLenBetween(customer.AdresPod_nrLok ?? "", 1, 15) || !matches(String(customer.AdresPod_nrLok ?? ""), REGEX.flatNo)) {
      e.push("Nr lokalu: wymagane poprawne pole.");
    }
    if (!hasLenBetween(customer.AdresPod_poczta ?? "", 1, 70) || !matches(String(customer.AdresPod_poczta ?? ""), REGEX.postOffice)) {
      e.push("Poczta: wymagane poprawne pole.");
    }

    if (!optionalMatch(customer.InvoiceEmail, REGEX.invoiceEmail)) {
      e.push("Email do faktur: pusty albo poprawny email.");
    }

    if (!optionalMatch(customer.BankAccount, REGEX.bankAccount)) {
      e.push("Konto bankowe: puste albo poprawny format.");
    }

    return e;
  }, [customer, nipLocked, nazwaPodLocked, kodUsLocked]);

  useEffect(() => {
    if (!customer) {
      setAvailableToSave(false);
      return;
    }
    setAvailableToSave(validateEditable(customer) && kasaErrors.length === 0);
  }, [customer, kasaErrors.length]);

  const setField = (key, value) => {
    setChanged(true);
    setCustomer((prev) => ({ ...prev, [key]: value }));
  };

  const toNumberOrNull = (val) =>
    val === "" || val === null || typeof val === "undefined" ? null : Number(val);

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Nie udało się odczytać pliku"));
      reader.readAsText(file, "utf-8");
    });

  const handleCertificateFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".crt")) {
      notification("error", "Dozwolony jest tylko plik .crt");
      return;
    }

    try {
      const text = await readFileAsText(file);
      setField("KsefCertificate", text);
      setCertificateFileName(file.name);
    } catch {
      notification("error", "Nie udało się odczytać pliku .crt");
    }
  };

  const handlePrivateKeyFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".key")) {
      notification("error", "Dozwolony jest tylko plik .key");
      return;
    }

    try {
      const text = await readFileAsText(file);
      setField("KsefPrivateKey", text);
      setPrivateKeyFileName(file.name);
    } catch {
      notification("error", "Nie udało się odczytać pliku .key");
    }
  };

  const save = () => {
    const mprEmpty = customer?.MinimumProfitabilityRate === null ||
      customer?.MinimumProfitabilityRate === "" ||
      typeof customer?.MinimumProfitabilityRate === "undefined";

    const cpsEmpty = customer?.CriticalProductState === null ||
      customer?.CriticalProductState === "" ||
      typeof customer?.CriticalProductState === "undefined";

    if (mprEmpty && !cpsEmpty) {
      notification("error", "Wprowadź minimalną marżę (%)");
      return;
    }
    if (cpsEmpty && !mprEmpty) {
      notification("error", "Wprowadź minimalny poziom produktu (%)");
      return;
    }

    if (!nipLocked && !matches(String(customer.NIP ?? ""), REGEX.nip)) {
      notification("error", "NIP musi mieć dokładnie 10 cyfr");
      return;
    }

    if (!nazwaPodLocked && !hasLenBetween(String(customer.NazwaPod ?? ""), 1, 256)) {
      notification("error", "Pełna nazwa podmiotu jest wymagana");
      return;
    }

    if (!kodUsLocked && !String(customer.KodUS || "").trim()) {
      notification("error", "Wybierz Urząd Skarbowy");
      return;
    }

    if (!inRangeOrEmpty(customer.MinimumProfitabilityRate, 0, 10000)) {
      notification("error", "Minimalna marża musi być w zakresie 0-10000");
      return;
    }
    if (!inRangeOrEmpty(customer.CriticalProductState, 0, 50)) {
      notification("error", "Minimalny poziom produktu musi być w zakresie 0-50");
      return;
    }
    if (!hasLenBetween(String(customer.AdresPod_ulica ?? ""), 1, 70) || !matches(String(customer.AdresPod_ulica ?? ""), REGEX.street)) {
      notification("error", "Podaj poprawną ulicę");
      return;
    }
    if (!hasLenBetween(String(customer.AdresPod_miejsc ?? ""), 1, 70) || !matches(String(customer.AdresPod_miejsc ?? ""), REGEX.city)) {
      notification("error", "Podaj poprawną miejscowość");
      return;
    }
    if (!matches(String(customer.AdresPod_kodPoczt ?? ""), REGEX.postalCode)) {
      notification("error", "Kod pocztowy musi mieć format NN-NNN");
      return;
    }
    if (!hasLenBetween(String(customer.AdresPod_nrDomu ?? ""), 1, 15) || !matches(String(customer.AdresPod_nrDomu ?? ""), REGEX.houseNo)) {
      notification("error", "Podaj poprawny numer domu");
      return;
    }
    if (!hasLenBetween(String(customer.AdresPod_nrLok ?? ""), 1, 15) || !matches(String(customer.AdresPod_nrLok ?? ""), REGEX.flatNo)) {
      notification("error", "Podaj poprawny numer lokalu");
      return;
    }
    if (!hasLenBetween(String(customer.AdresPod_poczta ?? ""), 1, 70) || !matches(String(customer.AdresPod_poczta ?? ""), REGEX.postOffice)) {
      notification("error", "Podaj poprawną pocztę");
      return;
    }

    if (!optionalMatch(customer.InvoiceEmail, REGEX.invoiceEmail)) {
      notification("error", "Email do faktur musi być pusty albo poprawny");
      return;
    }
    if (!optionalMatch(customer.BankAccount, REGEX.bankAccount)) {
      notification("error", "Konto bankowe musi być puste albo mieć poprawny format");
      return;
    }

    setSaving(true);
    setErr("");
    fetchMssqlApi(
      "customer",
      {
        method: "PUT",
        data: {
          Name: ((customer.Name).trim() || null),
          NIP: customer.NIP,
          NazwaPod: customer.NazwaPod,
          MinimumProfitabilityRate: toNumberOrNull(customer.MinimumProfitabilityRate),
          CriticalProductState: toNumberOrNull(customer.CriticalProductState),
          KodUS: customer.KodUS,
          AdresPod_ulica: customer.AdresPod_ulica,
          AdresPod_miejsc: customer.AdresPod_miejsc,
          AdresPod_kodPoczt: customer.AdresPod_kodPoczt,
          AdresPod_nrDomu: customer.AdresPod_nrDomu,
          AdresPod_nrLok: customer.AdresPod_nrLok,
          AdresPod_poczta: customer.AdresPod_poczta,
          BankAccount: customer.BankAccount,
          InvoiceEmail: customer.InvoiceEmail,
          KsefKeyPassword: customer.KsefKeyPassword,
          KsefCertificate: customer.KsefCertificate,
          KsefPrivateKey: customer.KsefPrivateKey,
        },
      },
      () => {
        setSaving(false);
        setChanged(false);
        load();
      },
      () => {
        setSaving(false);
      }
    );
  };

  if (!customer) return <div className="h5"></div>;

  return (
    <div>
      <Prompt
        when={changed}
        message="Istnieją niezapisane zmiany, czy na pewno chcesz kontynuować?"
      />

      <div className="card mb-3 mt-4">
        <div className="card-body">
          {err && <div className="alert alert-danger">{err}</div>}

          {kasaErrors.length > 0 && (
            <div className="alert alert-warning">
              <div className="font-weight-bold mb-1">Dane wymagane dla kasy fiskalnej:</div>
              <ul className="mb-0">
                {kasaErrors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          <table className="table table-striped text-center">
            <tbody>
              <tr>
                <td colSpan={3} className="text-left">Nazwa (potoczna)</td>
              </tr>
              <tr>
                <td colSpan={3} className="text-left">
                  <input
                    className={`form-control form-control-sm ${!customer.Name && "invalid-input"}`}
                    type="text"
                    value={customer.Name || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.length > 150) {
                        notification("error", "Nazwa może mieć maksymalnie 150 znaków");
                        return;
                      }
                      setField("Name", val);
                    }}
                    maxLength={151}
                  />
                </td>
              </tr>

              <tr>
                <td>Minimalna marża (%)</td>
                <td>Minimalny poziom produktu (%)</td>
                <td>Zgoda na regulamin</td>
              </tr>
              <tr>
                <td className="text-end align-middle">
                  <div className="mx-auto" style={{ maxWidth: 140 }}>
                    <input
                      className={`form-control form-control-sm ${!customer.MinimumProfitabilityRate && "invalid-input"}`}
                      type="number"
                      min={0}
                      max={10000}
                      step="0.01"
                      value={customer.MinimumProfitabilityRate ?? ""}
                      placeholder="0-10000"
                      onChange={(e) => setField("MinimumProfitabilityRate", e.target.value)}
                    />
                  </div>
                </td>
                <td className="text-end align-middle">
                  <div className="mx-auto" style={{ maxWidth: 140 }}>
                    <input
                      className={`form-control form-control-sm ${!customer.CriticalProductState && "invalid-input"}`}
                      type="number"
                      min={0}
                      max={50}
                      step="0.01"
                      value={customer.CriticalProductState ?? ""}
                      placeholder="0-50"
                      onChange={(e) => setField("CriticalProductState", e.target.value)}
                    />
                  </div>
                </td>
                <td className="text-end align-middle">
                  {getCheckboxFromBool(customer.RegulaminZgoda)}
                </td>
              </tr>

              <tr>
                <td>NIP</td>
                <td>Pełna nazwa podmiotu</td>
                <td>Urząd Skarbowy</td>
              </tr>
              <tr>
                <td>
                  {nipLocked ? (
                    <input
                      className="form-control form-control-sm"
                      value={customer.NIP || ""}
                      disabled
                    />
                  ) : (
                    <>
                      <input
                        className={`form-control form-control-sm ${
                          !matches(String(customer.NIP ?? ""), REGEX.nip) ? "invalid-input" : ""
                        }`}
                        value={customer.NIP || ""}
                        onChange={(e) => {
                          const val = String(e.target.value || "").replace(/[^\d]/g, "").slice(0, 10);
                          setField("NIP", val);
                        }}
                      />
                      <small className="text-muted d-block mt-1 text-left">
                        Po pierwszym zapisie pole zostanie zablokowane.
                      </small>
                    </>
                  )}
                </td>

                <td>
                  {nazwaPodLocked ? (
                    <input
                      className="form-control form-control-sm"
                      value={customer.NazwaPod || ""}
                      disabled
                    />
                  ) : (
                    <>
                      <input
                        className={`form-control form-control-sm ${
                          !hasLenBetween(customer.NazwaPod ?? "", 1, 256) ? "invalid-input" : ""
                        }`}
                        value={customer.NazwaPod || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length > 256) {
                            notification("error", "Pełna nazwa podmiotu może mieć maksymalnie 256 znaków");
                            return;
                          }
                          setField("NazwaPod", val);
                        }}
                      />
                      <small className="text-muted d-block mt-1 text-left">
                        Po pierwszym zapisie pole zostanie zablokowane.
                      </small>
                    </>
                  )}
                </td>

                <td>
                  {kodUsLocked ? (
                    <input
                      className="form-control form-control-sm"
                      value={kodUsDisplay || ""}
                      disabled
                    />
                  ) : (
                    <>
                      <select
                        className={`form-control form-control-sm ${!customer.KodUS ? "invalid-input" : ""}`}
                        value={customer.KodUS || ""}
                        onChange={(e) => {
                          setField("KodUS", e.target.value);
                          if (e.target.value) {
                            notification("warning", "Po zapisaniu nie będzie można zmienić Urzędu Skarbowego");
                          }
                        }}
                      >
                        <option value="">Wybierz</option>
                        {us.map((us) => {
                          const kod = us.KodUs ?? us.KodUS;
                          return (
                            <option key={kod} value={kod}>
                              {`${us.Nazwa} [${kod}]`}
                            </option>
                          );
                        })}
                      </select>
                      <small className="text-muted d-block mt-1 text-left">
                        Po pierwszym zapisie pole zostanie zablokowane.
                      </small>
                    </>
                  )}
                </td>
              </tr>

              <tr>
                <td>Ulica</td>
                <td>Miejscowość</td>
                <td>Kod pocztowy</td>
              </tr>
              <tr>
                <td>
                  <input
                    className={`form-control form-control-sm ${
                      !hasLenBetween(customer.AdresPod_ulica ?? "", 1, 70) || !matches(String(customer.AdresPod_ulica ?? ""), REGEX.street)
                        ? "invalid-input"
                        : ""
                    }`}
                    value={customer.AdresPod_ulica || ""}
                    onChange={(e) => setField("AdresPod_ulica", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className={`form-control form-control-sm ${
                      !hasLenBetween(customer.AdresPod_miejsc ?? "", 1, 70) || !matches(String(customer.AdresPod_miejsc ?? ""), REGEX.city)
                        ? "invalid-input"
                        : ""
                    }`}
                    value={customer.AdresPod_miejsc || ""}
                    onChange={(e) => setField("AdresPod_miejsc", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className={`form-control form-control-sm text-end ${
                      !matches(String(customer.AdresPod_kodPoczt ?? ""), REGEX.postalCode) ? "invalid-input" : ""
                    }`}
                    value={customer.AdresPod_kodPoczt || ""}
                    onChange={(e) => setField("AdresPod_kodPoczt", e.target.value)}
                  />
                </td>
              </tr>

              <tr>
                <td>Nr domu</td>
                <td>Nr lokalu</td>
                <td>Poczta</td>
              </tr>
              <tr>
                <td>
                  <input
                    className={`form-control form-control-sm ${
                      !hasLenBetween(customer.AdresPod_nrDomu ?? "", 1, 15) || !matches(String(customer.AdresPod_nrDomu ?? ""), REGEX.houseNo)
                        ? "invalid-input"
                        : ""
                    }`}
                    value={customer.AdresPod_nrDomu || ""}
                    onChange={(e) => setField("AdresPod_nrDomu", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className={`form-control form-control-sm ${
                      !hasLenBetween(customer.AdresPod_nrLok ?? "", 1, 15) || !matches(String(customer.AdresPod_nrLok ?? ""), REGEX.flatNo)
                        ? "invalid-input"
                        : ""
                    }`}
                    value={customer.AdresPod_nrLok || ""}
                    onChange={(e) => setField("AdresPod_nrLok", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className={`form-control form-control-sm ${
                      !hasLenBetween(customer.AdresPod_poczta ?? "", 1, 70) || !matches(String(customer.AdresPod_poczta ?? ""), REGEX.postOffice)
                        ? "invalid-input"
                        : ""
                    }`}
                    value={customer.AdresPod_poczta || ""}
                    onChange={(e) => setField("AdresPod_poczta", e.target.value)}
                  />
                </td>
              </tr>

              <tr>
                <td>Konto bankowe</td>
                <td>Email do faktur</td>
                <td>Hasło KSeF</td>
              </tr>
              <tr>
                <td>
                  <input
                    className={`form-control form-control-sm ${
                      !optionalMatch(customer.BankAccount, REGEX.bankAccount) ? "invalid-input" : ""
                    }`}
                    value={customer.BankAccount || ""}
                    onChange={(e) => setField("BankAccount", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className={`form-control form-control-sm ${
                      !optionalMatch(customer.InvoiceEmail, REGEX.invoiceEmail) ? "invalid-input" : ""
                    }`}
                    value={customer.InvoiceEmail || ""}
                    onChange={(e) => setField("InvoiceEmail", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="form-control form-control-sm"
                    type="password"
                    value={customer.KsefKeyPassword || ""}
                    onChange={(e) => setField("KsefKeyPassword", e.target.value)}
                  />
                </td>
              </tr>

              <tr>
                <td>Certyfikat KSeF</td>
                <td>Private Key KSeF</td>
                <td></td>
              </tr>
              <tr>
                <td colSpan={3} className="pt-2">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <div className="d-flex align-items-center justify-content-between px-2 py-2 border">
                        <div className="text-left pr-3">
                          <div className="font-weight-bold">Certyfikat KSeF</div>
                          <div className="small text-muted">
                            {certificateFileName || "Nie wybrano pliku .crt"}
                          </div>
                        </div>

                        <div className="ml-2 d-flex justify-content-center">
                          <input
                            id="crtFile"
                            type="file"
                            accept=".crt"
                            style={{ display: "none" }}
                            onChange={handleCertificateFile}
                          />
                          <label
                            htmlFor="crtFile"
                            className="btn btn-primary btn-sm mb-0 d-inline-flex align-items-center justify-content-center"
                            style={{
                              borderRadius: 20,
                              padding: "0.375rem 0.9rem",
                              whiteSpace: "nowrap",
                              gap: "8px"
                            }}
                          >
                            <i className="fas fa-folder-open" />
                            <span>Wybierz plik</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 mb-2">
                      <div className="d-flex align-items-center justify-content-between px-2 py-2 border">
                        <div className="text-left pr-3">
                          <div className="font-weight-bold">Private Key KSeF</div>
                          <div className="small text-muted">
                            {privateKeyFileName || "Nie wybrano pliku .key"}
                          </div>
                        </div>

                        <div className="ml-2">
                          <input
                            id="keyFile"
                            type="file"
                            accept=".key"
                            style={{ display: "none" }}
                            onChange={handlePrivateKeyFile}
                          />
                          <label
                            htmlFor="keyFile"
                            className="btn btn-primary btn-sm mb-0 d-inline-flex align-items-center justify-content-center"
                            style={{
                              borderRadius: 20,
                              padding: "0.375rem 0.9rem",
                              whiteSpace: "nowrap",
                              gap: "8px"
                            }}
                          >
                            <i className="fas fa-folder-open" />
                            <span>Wybierz plik</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="position-fixed container" style={{ bottom: "0px" }}>
        <div
          className="card-footer text-center fixed-bottom"
          style={{ backgroundColor: "#cbcbcb" }}
        >
          <button
            disabled={saving || !changed || bothEmpty(customer) || !availableToSave}
            className="btn btn-primary w-20 btn-sm"
            onClick={save}
          >
            {saving ? "Zapisuję…" : "Zapisz"}
          </button>
        </div>
      </div>
    </div>
  );
}